import { useCallback, useMemo } from 'react';
import type { Product } from '@src/core/buyerApiClient';
import { useAppSelector } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import {
  adderResumeSelector,
  loadingProductSelector,
  productSelectionStateSelector,
  tariffsState,
} from '@src/stores/product';

export const useMultiProductSelection = (products: Product[]) => {
  const widgetId = useWidgetId();

  const {
    selectedTariffs,
    selectedTariffHour,
    productId: selectedProductId,
  } = useAppSelector(adderResumeSelector(widgetId));

  const { selectedDate } = useAppSelector(tariffsState(widgetId));
  const loadingProduct = useAppSelector(loadingProductSelector(selectedProductId));
  const productSelectionState = useAppSelector(productSelectionStateSelector(widgetId));

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedProductId),
    [products, selectedProductId],
  );

  const countFilledProducts = useMemo(
    () =>
      Object.values(productSelectionState).reduce((acc, cur) => {
        if ('currentProductAdderStep' in cur && cur.currentProductAdderStep === 'finish') {
          return acc + 1;
        }
        return acc;
      }, 0),
    [productSelectionState],
  );

  const isProductFilled = useCallback(
    (productId: string) => productSelectionState[productId]?.currentProductAdderStep === 'finish',
    [productSelectionState],
  );

  const getSelectedDate = useCallback(
    (productId: string) =>
      productSelectionState[productId]?.tariffsState?.selectedDate ??
      (productId === selectedProductId ? selectedDate : undefined),
    [productSelectionState, selectedDate, selectedProductId],
  );

  const getSelectedTime = useCallback(
    (productId: string) =>
      productSelectionState[productId]?.selectedTariffHour?.formattedTime ??
      (productId === selectedProductId ? selectedTariffHour?.formattedTime : undefined),
    [productSelectionState, selectedTariffHour, selectedProductId],
  );

  const getSelectedTariffs = useCallback(
    (productId: string) =>
      productSelectionState[productId]?.tariffsState?.listOfTariffsPerType.filter((tf) => tf.selectedQuantity) ||
      (productId === selectedProductId ? selectedTariffs : undefined),
    [productSelectionState, selectedTariffs, selectedProductId],
  );

  const shouldProductBeDisabled = useCallback(
    (productId: string) => {
      if (
        selectedProductId &&
        selectedProductId !== productId &&
        (!!getSelectedDate(selectedProductId) || !!getSelectedTariffs(selectedProductId)?.length) &&
        !isProductFilled(productId)
      ) {
        return true;
      }
      return false;
    },
    [selectedProductId, getSelectedDate, getSelectedTariffs, isProductFilled],
  );

  return {
    selectedProductId,
    selectedProduct,
    loadingProduct,
    countFilledProducts,
    isProductFilled,
    getSelectedDate,
    getSelectedTime,
    getSelectedTariffs,
    shouldProductBeDisabled,
  };
};
